import Link from "next/link"
import Image from "next/image"
import { ArrowRight, type LucideIcon } from "lucide-react"
import type { ReactNode } from "react"

/**
 * Tint preset for an icon tile.
 *  - `orange` → savings / appliance-purchase semantic (Fridge, Freezer, Rebate)
 *  - `blue`   → informational semantic (Recycling, Utility, Coverage)
 *  - `green`  → healthy / qualified semantic (Active rebates, ENERGY STAR)
 *  - `gray`   → neutral
 */
export type LocationStatTileTone = "orange" | "blue" | "green" | "gray"

export interface LocationStatTile {
  /** Lucide icon component to render inside the tile's white square */
  icon: LucideIcon
  /** Big number (or short string like "5+") rendered above the label */
  count: number | string
  /** Tiny uppercase label below the count, e.g. "Fridge", "Cities", "ZIPs" */
  label: string
  /** Color preset for the tile background tint + icon stroke */
  tone?: LocationStatTileTone
}

export interface LocationStatCardProps {
  /** Destination — the whole card is the click target */
  href: string
  /** Card title — usually a state or utility name */
  name: string
  /** Optional flag / thumbnail image URL (rendered 44×30 with rounded corners) */
  flagUrl?: string
  /** Alt text for the flag/thumbnail */
  flagAlt?: string
  /** Optional custom node in place of the flag (e.g., a brand logo component) */
  headerLeft?: ReactNode
  /** Small uppercase eyebrow above the headline value (e.g. "Up to", "Avg rate") */
  headerEyebrow?: string
  /** Big number rendered in Fridge orange — accepts string for pre-formatted values */
  headerValue?: number | string
  /** Small prefix at the top-left of the value (e.g. "$") */
  headerValuePrefix?: string
  /** Optional small suffix after the value (e.g. "/yr", "¢") */
  headerValueSuffix?: string
  /** 1–3 icon tiles. Tiles with count=0 are omitted; the grid auto-fits. */
  tiles?: LocationStatTile[]
  /** Footer text — wrapped in a blue link with arrow */
  footerLabel: string
  /** Additional classes on the outer link */
  className?: string
}

const TONE_BG: Record<LocationStatTileTone, string> = {
  orange: "rgba(255, 153, 81, 0.10)",
  blue: "rgba(4, 105, 240, 0.08)",
  green: "rgba(16, 166, 92, 0.10)",
  gray: "rgba(107, 114, 128, 0.08)",
}

const TONE_STROKE: Record<LocationStatTileTone, string> = {
  orange: "#ff9951",
  blue: "#0469f0",
  green: "#10a65c",
  gray: "#6b7280",
}

/**
 * LocationStatCard — V5 Icon Squares card from the 2026-05-20 design
 * handoff. A flag/thumbnail + headline stat + icon-tile breakdown +
 * footer link. Built first for the `/rebates` state grid; reused on
 * `/local` and any other state/utility/place-level listing.
 *
 * Tiles with `count: 0` are filtered out and the grid auto-fits the
 * remaining 1–3 tiles. Pass `headerLeft` to substitute a custom node
 * (like a utility logo) for the flag.
 *
 * Design spec: see `docs/Design.md` §5 and the handoff bundle
 * `design_handoff_state_rebate_card/README.md`.
 */
export function LocationStatCard({
  href,
  name,
  flagUrl,
  flagAlt,
  headerLeft,
  headerEyebrow,
  headerValue,
  headerValuePrefix,
  headerValueSuffix,
  tiles = [],
  footerLabel,
  className = "",
}: LocationStatCardProps) {
  // Filter zero-count tiles so the grid auto-fits 1–3 non-empty tiles.
  const visibleTiles = tiles.filter((t) => {
    if (typeof t.count === "number") return t.count > 0
    return Boolean(t.count)
  })

  return (
    <Link
      href={href}
      className={`group block bg-white border border-gray-200 rounded-xl transition-all duration-[0.25s] ease-out hover:-translate-y-0.5 hover:border-[#ff9951] hover:shadow-[0_12px_24px_-8px_rgba(0,0,0,0.10),0_4px_8px_-4px_rgba(0,0,0,0.06)] ${className}`}
      style={{ padding: 22, color: "inherit" }}
    >
      <div className="flex flex-col" style={{ gap: 16 }}>
        {/* HEADER ─────────────────────────────────────────────────────── */}
        <div className="flex items-center justify-between" style={{ gap: 12 }}>
          <div className="flex items-center min-w-0" style={{ gap: 12 }}>
            {headerLeft ?? (flagUrl ? (
              <Image
                src={flagUrl}
                alt={flagAlt ?? `Flag of ${name}`}
                width={44}
                height={30}
                className="flex-shrink-0"
                style={{
                  borderRadius: 4,
                  boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.08)",
                }}
              />
            ) : null)}
            <div className="min-w-0">
              <h3
                className="text-gray-900 truncate"
                style={{
                  fontSize: 20,
                  fontWeight: 900,
                  letterSpacing: "-0.02em",
                  lineHeight: 1.1,
                  margin: 0,
                }}
              >
                {name}
              </h3>
            </div>
          </div>
          {(headerEyebrow || headerValue !== undefined) && (
            <div className="text-right flex-shrink-0">
              {headerEyebrow && (
                <div
                  style={{
                    fontSize: 9,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    color: "#9a6a3a",
                  }}
                >
                  {headerEyebrow}
                </div>
              )}
              {headerValue !== undefined && (
                <div
                  className="text-[#ff9951]"
                  style={{
                    fontSize: 24,
                    fontWeight: 900,
                    letterSpacing: "-0.03em",
                    lineHeight: 1,
                  }}
                >
                  {/* FRIDGE-766: prefix and value are ONE text node. As two
                      ("$" in its own span, then the number) React separates
                      them with a comment and a text-node-joining extractor
                      reads "Up to $ 85" — an empty price and a bare number.
                      The smaller-$ typography was not worth a dishonest read. */}
                  {`${headerValuePrefix ?? ""}${headerValue}`}
                  {headerValueSuffix && (
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 700,
                        marginLeft: 2,
                        letterSpacing: "0.02em",
                      }}
                    >
                      {headerValueSuffix}
                    </span>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* BREAKDOWN — icon tiles ─────────────────────────────────────── */}
        {/* FRIDGE-1068: the owner saw the third tile hanging off the card
            edge on his phone ("hanging off. looks super sloppy. fix
            sitewide"). Cause: `repeat(N, 1fr)` tracks cannot shrink below
            each tile's min-content (icon + gap + the word RECYCLING), and
            three of those plus two gaps were wider than the card — by 81px
            at 375px, and by 4px even at 1280px.
            Fix, measured against the grid's own width (a container query,
            so every consumer of this card gets it):
              - columns are `auto-fit, minmax(88px, auto)`: three tiles share
                a row wherever three 88px columns fit, wrap to 2 + 1 below
                that (320px phones), and a lone tile still spans the row.
                The `auto` max sizes each column to its own label (RECYCLING
                is wider than FRIDGE) and stretches the remainder equally —
                equal `1fr` columns squeezed RECYCLING into an ellipsis at
                1280px;
              - under 344px of grid width the icon sits ABOVE the number and
                label (min width = the label), at 344px and up it sits to the
                left as designed; the tile overhead is 10px padding / 8px gap
                / 28px icon so three icon-left tiles fit a 1280px card;
              - the label truncates with an ellipsis as the last resort, so
                nothing can cross the card border again. */}
        {visibleTiles.length > 0 && (
          <div
            className="grid @container"
            style={{
              gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 88px), auto))",
              gap: 8,
            }}
          >
            {visibleTiles.map((tile, i) => {
              const tone = tile.tone ?? "orange"
              const Icon = tile.icon
              return (
                <div
                  key={`${tile.label}-${i}`}
                  className="flex min-w-0 flex-col items-start gap-1.5 @min-[344px]:flex-row @min-[344px]:items-center @min-[344px]:gap-2"
                  style={{
                    background: TONE_BG[tone],
                    borderRadius: 8,
                    padding: 10,
                  }}
                >
                  <div
                    className="inline-flex items-center justify-center bg-white flex-shrink-0"
                    style={{ width: 28, height: 28, borderRadius: 7 }}
                  >
                    <Icon
                      size={16}
                      strokeWidth={2}
                      color={TONE_STROKE[tone]}
                      aria-hidden
                    />
                  </div>
                  <div className="w-full min-w-0">
                    <div
                      className="text-gray-900"
                      style={{
                        fontSize: 18,
                        fontWeight: 900,
                        letterSpacing: "-0.02em",
                        lineHeight: 1,
                      }}
                    >
                      {tile.count}
                    </div>
                    <div
                      className="truncate"
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        color: "#696f7d", // FRIDGE-1504 L1504-5: 4.50+:1 on every tile tint (#6b7280 gave 4.09-4.33)
                        marginTop: 2,
                      }}
                    >
                      {tile.label}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {/* FOOTER ─────────────────────────────────────────────────────── */}
        <div style={{ borderTop: "1px solid #f3f4f6", paddingTop: 12 }}>
          <span
            className="inline-flex items-center text-[#0469f0]"
            style={{ fontSize: 13, fontWeight: 700, gap: 6 }}
          >
            {footerLabel}
            <ArrowRight size={14} strokeWidth={2.5} />
          </span>
        </div>
      </div>
    </Link>
  )
}
